import React, { useState, useEffect } from 'react';
import { Box, Container, Paper, Button, Typography, Snackbar, Alert } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import WizardSidebar from './WizardSidebar';
import WizardStep from './WizardStep';
import WizardNavigation from './WizardNavigation';
import { wizardService } from '../../services/wizardService';
import { authService } from '../../services/authService';
import { ReviewSubmitData } from '../../types/forms';

// Import form components
import BasicInfo from '../forms/BasicInfo';
import EmploymentIncomeStep from '../forms/EmploymentIncomeStep';
import BusinessRental from '../forms/BusinessRental';
import Deductions from '../forms/Deductions';
import Dependents from '../forms/Dependents';
import SpouseInfo from '../forms/SpouseInfo';
import DocumentUpload from '../forms/DocumentUpload';
import PriorTaxInfo from '../forms/PriorTaxInfo';
import ReviewSubmit from '../forms/ReviewSubmit';

const WizardContainer: React.FC = () => {
  const navigate = useNavigate();
  const user = authService.getCurrentUser();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<any>({});
  const [lastSaved, setLastSaved] = useState('Just now');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadProgress = async () => {
      if (user?.id) {
        try {
          const progress = await wizardService.getProgress();
          setCurrentStep(progress.currentStep);
          setFormData(JSON.parse(progress.formData));
          setLastSaved(new Date(progress.lastUpdated).toLocaleString());
        } catch (error) {
          console.error('Error loading progress:', error);
        }
      }
    };
    loadProgress();
  }, [user?.id]);

  const handleSaveProgress = async () => {
    if (!user?.id) return;
    
    setIsSaving(true);
    try {
      await wizardService.saveProgress(currentStep, formData);
      setLastSaved(new Date().toLocaleString());
    } catch (error) {
      console.error('Error saving progress:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const validateCurrentStep = async (): Promise<boolean> => {
    if (!user?.id) return false;
    
    try {
      console.log('Validating step', currentStep, 'with formData:', formData);
      const isValid = await wizardService.validateStep(currentStep, formData);
      console.log('Validation result:', isValid);
      return isValid;
    } catch (error) {
      console.error('Error validating step:', error);
      return false;
    }
  };

  const handleStepChange = async (step: number) => {
    if (step > currentStep) {
      // Only validate when moving forward
      try {
        const isValid = await validateCurrentStep();
        if (!isValid) {
          setError('Please complete all required fields before proceeding');
          return;
        }
        await handleSaveProgress();
        setCurrentStep(step);
      } catch (error) {
        setError('Error saving progress. Please try again.');
      }
    } else {
      // Moving backward doesn't require validation
      setCurrentStep(step);
      handleSaveProgress();
    }
  };

  const handleComplete = async () => {
    if (!user?.id) return;
    
    try {
      const isValid = await validateCurrentStep();
      if (!isValid) {
        setError('Please complete all required fields before submitting');
        return;
      }
      await wizardService.markAsCompleted();
      navigate('/dashboard');
    } catch (error) {
      setError('Error completing wizard. Please try again.');
    }
  };

  const handleNext = (data: ReviewSubmitData) => {
    handleStepChange(data.currentStep);
  };

  const handleBack = () => {
    handleStepChange(currentStep - 1);
  };

  // Function to render the appropriate form component based on current step
  const renderFormComponent = () => {
    const handleFormNext = (data: any) => {
      setFormData({ ...formData, ...data });
      handleStepChange(currentStep + 1);
    };

    const handleDataChange = (data: any) => {
      setFormData({ ...formData, ...data });
    };

    switch (currentStep) {
      case 1:
        return <BasicInfo data={formData} onNext={handleFormNext} onDataChange={handleDataChange} />;
      case 2:
        return <EmploymentIncomeStep data={formData} onNext={handleFormNext} onDataChange={handleDataChange} />;
      case 3:
        return <BusinessRental data={formData} onNext={handleFormNext} onDataChange={handleDataChange} />;
      case 4:
        return <Deductions data={formData} onNext={handleFormNext} onDataChange={handleDataChange} />;
      case 5:
        return <Dependents data={formData} onNext={handleFormNext} onDataChange={handleDataChange} />;
      case 6:
        return <SpouseInfo data={formData} onNext={handleFormNext} onDataChange={handleDataChange} />;
      case 7:
        return <DocumentUpload data={formData} onNext={handleFormNext} onDataChange={handleDataChange} />;
      case 8:
        return <PriorTaxInfo data={formData} onNext={handleFormNext} onDataChange={handleDataChange} />;
      case 9:
        return <ReviewSubmit data={formData} onNext={handleFormNext} onDataChange={handleDataChange} />;
      default:
        return <BasicInfo data={formData} onNext={handleFormNext} onDataChange={handleDataChange} />;
    }
  };

  // Function to get step title and description
  const getStepInfo = () => {
    switch (currentStep) {
      case 1:
        return { title: "Basic Information", description: "Please provide your personal information." };
      case 2:
        return { title: "Employment Income", description: "Enter your employment and income details." };
      case 3:
        return { title: "Business & Rental Information", description: "Provide business and rental property details." };
      case 4:
        return { title: "Deductions", description: "Enter your deductions and expenses." };
      case 5:
        return { title: "Dependents", description: "Add information about your dependents." };
      case 6:
        return { title: "Spouse Information", description: "Provide your spouse's information." };
      case 7:
        return { title: "Document Upload", description: "Upload required documents." };
      case 8:
        return { title: "Prior Tax Information", description: "Enter your prior year tax information." };
      case 9:
        return { title: "Review & Submit", description: "Review all information before submitting." };
      default:
        return { title: "Basic Information", description: "Please provide your personal information." };
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Paper 
        elevation={3} 
        sx={{ 
          display: 'flex',
          flexDirection: 'column',
          minHeight: '80vh',
          overflow: 'hidden',
          borderRadius: 2,
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
        }}
      >
        {/* Header */}
        <Box sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          padding: '30px 40px',
          textAlign: 'center',
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8
        }}>
          <h1 style={{ 
            fontSize: '2rem', 
            marginBottom: '10px', 
            fontWeight: 300 
          }}>
            Tax Onboarding Wizard
          </h1>
          <p style={{ 
            opacity: 0.9, 
            fontSize: '1rem' 
          }}>
            Streamlined tax data collection for a better client experience
          </p>
        </Box>

        {/* Main Content Area */}
        <Box sx={{ 
          display: 'flex',
          flex: 1,
          background: 'white'
        }}>
          <WizardSidebar currentStep={currentStep} onStepChange={handleStepChange} />
          <Box sx={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column',
            background: 'white'
          }}>
            {/* Save Progress Bar */}
            <Box sx={{
              background: '#f8f9fa',
              px: 5,
              py: 1.5,
              borderBottom: '1px solid #e9ecef',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    background: isSaving ? '#ffc107' : '#28a745',
                    mr: 1
                  }} />
                  <Typography variant="body2" color={isSaving ? '#ffc107' : '#28a745'} fontWeight={500}>
                    {isSaving ? 'Saving...' : 'All changes saved'}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Last saved: {lastSaved}
                </Typography>
              </Box>
              <Button
                variant="contained"
                onClick={handleSaveProgress}
                disabled={isSaving}
                sx={{
                  background: '#6c757d',
                  color: 'white',
                  borderRadius: 1,
                  px: 3,
                  py: 1.2,
                  fontSize: 14,
                  textTransform: 'none',
                  boxShadow: 'none',
                  '&:hover': { background: '#5a6268' }
                }}
              >
                💾 Save & Finish Later
              </Button>
            </Box>

            {/* Main Content */}
            <WizardStep 
              currentStep={currentStep}
              totalSteps={9}
              title={getStepInfo().title}
              description={getStepInfo().description}
              onNext={handleNext}
              onBack={handleBack}
              showButtons={currentStep !== 1}
            >
              {renderFormComponent()}
            </WizardStep>
            <WizardNavigation 
              currentStep={currentStep}
              onStepChange={handleStepChange}
              onComplete={handleComplete}
            />
          </Box>
        </Box>

        {/* Add Snackbar for error messages */}
        <Snackbar 
          open={!!error} 
          autoHideDuration={6000} 
          onClose={() => setError(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
            {error}
          </Alert>
        </Snackbar>
      </Paper>
    </Container>
  );
};

export default WizardContainer; 